import { type TransferListItem, type TransferPhoto } from '@utazzo/shared';
import { UzButton, UzCheckbox, UzConfirmDialog, UzInput, UzSpinner } from '@utazzo/ui';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import React, { type ReactNode, useMemo, useState } from 'react';

import { listTransfers } from '../transfers.api';
import { useTransferGeoFilters } from '../use-transfer-geo-filters';
import { useBulkApplyTransferMedia, useTransfersList } from '../use-transfers';

import { DataGridFilterChip } from '@/components/data-grid';
import { fetchAllPages } from '@/components/list-screen';
import { FixedMediaSlotsField } from '@/features/website-cms/components/FixedMediaSlotsField';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

const PAGE_SIZE = 20;
const MIN_SEARCH_LENGTH = 3;
const GALLERY_COUNT = 4;

/** Shopify-style card: title header + stacked body (mirrors the edit page). */
const Card = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): React.JSX.Element => (
  <section className="rounded-lg border border-zinc-200 bg-white">
    <header className="border-b border-zinc-100 px-5 py-3.5">
      <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
    </header>
    <div className="space-y-5 px-5 py-5">{children}</div>
  </section>
);

/**
 * Transfers -> Media bulk-assign tool.
 *
 * Left: a searchable, checkbox list of transfers (single / multi / select-all).
 * Right: the same Cover + Gallery widgets as the edit page. Choosing a cover +
 * all 4 gallery images and hitting Apply overwrites that image set on every
 * selected transfer (partial PATCH per id - see `bulkApplyTransferMedia`).
 */
export const TransfersMediaManager = (): React.JSX.Element => {
  const [searchInput, setSearchInput] = useState('');
  const debounced = useDebouncedValue(searchInput.trim(), 300);
  const search = debounced.length >= MIN_SEARCH_LENGTH ? debounced : '';
  const [page, setPage] = useState(1);

  // Country filter (shared with the Listing tab).
  const { countryFilter } = useTransferGeoFilters();
  const [countryId, setCountryId] = useState('');

  // Selection is a set of transfer ids, kept across pages / searches.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectingAll, setSelectingAll] = useState(false);

  // Chosen images to broadcast to the selected transfers.
  const [coverMediaId, setCoverMediaId] = useState<string | null>(null);
  const [galleryImages, setGalleryImages] = useState<string[]>([]);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const query = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      sortBy: 'createdAt',
      sortDir: 'desc' as const,
      ...(search ? { search } : {}),
      ...(countryId ? { countryId } : {}),
    }),
    [page, search, countryId],
  );

  // Reset to page 1 whenever the (debounced) search changes.
  const listQuery = useTransfersList(query);
  const rows = listQuery.data?.data ?? [];
  const totalItems = listQuery.data?.pagination.totalItems ?? 0;
  const totalPages = listQuery.data?.pagination.totalPages ?? 1;

  const applyMut = useBulkApplyTransferMedia();

  const toggleRow = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Header checkbox reflects the full matching set, not just this page.
  const allSelected = totalItems > 0 && selected.size >= totalItems;
  const someSelected = selected.size > 0 && !allSelected;

  const handleSelectAll = async (): Promise<void> => {
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    // Select every transfer matching the current search, across all pages.
    setSelectingAll(true);
    try {
      const all = await fetchAllPages<TransferListItem, typeof query>(listTransfers, query);
      setSelected(new Set(all.map((r) => r.id)));
    } finally {
      setSelectingAll(false);
    }
  };

  const onSearchChange = (value: string): void => {
    setSearchInput(value);
    setPage(1);
  };

  const coverReady = Boolean(coverMediaId);
  const galleryReady = galleryImages.length === GALLERY_COUNT;
  const canApply = selected.size > 0 && coverReady && galleryReady && !applyMut.isPending;

  const applyNow = async (): Promise<void> => {
    if (!coverMediaId) return;
    const photos: TransferPhoto[] = [{ mediaId: coverMediaId, sortOrder: 0 }];
    await applyMut.mutateAsync({
      ids: [...selected],
      photos,
      galleryImageMediaIds: galleryImages,
    });
    // Keep the chosen images (handy for the next batch); clear the selection.
    setSelected(new Set());
  };

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
      {/* ---- Left: searchable transfer list ---- */}
      <div className="flex flex-col rounded-lg border border-zinc-200 bg-white">
        <div className="space-y-2.5 border-b border-zinc-100 p-3">
          <UzInput
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search transfers by name, code, city, route or vendor"
            leftAdornment={<Search className="h-4 w-4 text-zinc-400" />}
          />
          <div className="flex flex-wrap items-center gap-2">
            <DataGridFilterChip
              filter={countryFilter}
              value={countryId}
              onChange={(v) => {
                setCountryId(v);
                setPage(1);
              }}
            />
          </div>
        </div>

        {/* Select-all + selection count */}
        <div className="flex items-center justify-between gap-2 border-b border-zinc-100 px-4 py-2.5">
          <label className="flex items-center gap-2 text-sm text-zinc-700">
            <UzCheckbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              disabled={selectingAll || totalItems === 0}
              onCheckedChange={() => void handleSelectAll()}
            />
            <span>{selectingAll ? 'Selecting all...' : 'Select all'}</span>
          </label>
          <span className="text-xs text-zinc-500">
            {selected.size > 0 ? `${selected.size} selected` : `${totalItems} transfers`}
          </span>
        </div>

        {/* List body */}
        <div className="max-h-[520px] min-h-[240px] overflow-y-auto">
          {listQuery.isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <UzSpinner />
            </div>
          ) : listQuery.isError ? (
            <div className="p-6 text-center text-sm text-red-600">
              Failed to load transfers. Try again.
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">
              {search ? 'No transfers match this search.' : 'No transfers yet.'}
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {rows.map((row) => {
                const checked = selected.has(row.id);
                return (
                  <li key={row.id}>
                    <label
                      className={
                        'flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-zinc-50 ' +
                        (checked ? 'bg-zinc-50' : '')
                      }
                    >
                      <UzCheckbox
                        checked={checked}
                        onCheckedChange={() => toggleRow(row.id)}
                        containerClassName="mt-0.5"
                      />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium text-zinc-900">
                          {row.name}
                        </span>
                        <span className="truncate text-xs text-zinc-500">
                          {(row.fromLocationName ?? 'unset') +
                            ' -> ' +
                            (row.toLocationName ?? 'unset')}
                          {' · '}
                          {row.cityName}, {row.countryName}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-zinc-100 px-4 py-2.5 text-xs text-zinc-500">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <UzButton
                variant="ghost"
                size="sm"
                disabled={page <= 1 || listQuery.isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </UzButton>
              <UzButton
                variant="ghost"
                size="sm"
                disabled={page >= totalPages || listQuery.isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </UzButton>
            </div>
          </div>
        )}
      </div>

      {/* ---- Right: image picker + apply ---- */}
      <div className="space-y-6">
        <Card title="Photos">
          <MediaDropField
            label="Cover photo"
            value={coverMediaId}
            onChange={setCoverMediaId}
            folder="transfers"
            variant="banner"
            disabled={applyMut.isPending}
          />
        </Card>

        <Card
          title="Gallery"
          description="Exactly 4 images. Drag a tile to reorder. All 4 are required."
        >
          <FixedMediaSlotsField
            value={galleryImages}
            onChange={setGalleryImages}
            count={GALLERY_COUNT}
            gridClassName="grid grid-cols-2 gap-3 sm:grid-cols-4"
            aspectClassName="aspect-square"
            folder="transfers"
            disabled={applyMut.isPending}
          />
        </Card>

        {/* Apply bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-white px-5 py-4">
          <div className="text-sm text-zinc-600">
            {selected.size === 0 ? (
              <span className="text-zinc-500">Select one or more transfers to apply images.</span>
            ) : !coverReady || !galleryReady ? (
              <span className="text-zinc-500">
                Add a cover photo and all 4 gallery images to continue.
              </span>
            ) : (
              <span>
                Overwrites the cover + gallery on{' '}
                <span className="font-semibold text-zinc-900">{selected.size}</span> selected
                transfer{selected.size === 1 ? '' : 's'}.
              </span>
            )}
          </div>
          <UzButton
            disabled={!canApply}
            loading={applyMut.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            Apply to {selected.size} transfer{selected.size === 1 ? '' : 's'}
          </UzButton>
        </div>
      </div>

      <UzConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Apply images to ${selected.size} transfer${selected.size === 1 ? '' : 's'}?`}
        description="This overwrites the cover photo and all 4 gallery images on every selected transfer. This can't be undone."
        confirmLabel="Apply"
        tone="primary"
        onConfirm={applyNow}
      />
    </div>
  );
};
